import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Messages } from "primereact/messages";
import { REIButton, REIDialoge, REIDivider, REIDropDown, REIInputText } from "../../../../components/ui";
import DatePicker from "../../../../components/ui/calender";
import { ICashFlow } from "../../../../interface/cashFlow";
import { getDateStringWithGivenFormat, getStartOfFinancialYear } from "../../../../utils/commonUtil";
import { MEMBER_KEY, USER_DETAIL } from "../../../../utils/constants";
import { httpPost } from "../../../../services/httpService";
import { ApiEndPoints } from "../../../../utils/endpoints";
import { Calendar } from "primereact/calendar";

interface PaymentRow {
  id: number;
  dateReceived: string;
  dateFrom: string;
  dateTo: string;
  amount?: number;
  notes: string;
  invalidFields?: string[];
  isNewRow?: boolean;
  address?: string;
  group?: string;
}

const RentEditForm: React.FC<{
  cashFlowData: ICashFlow | undefined;
  portfolioTrackerId: number | undefined;
  propertyName: string | undefined;
  setIsEditable: (isEditable: boolean) => void | undefined;
  onCancel: () => void | undefined;
}> = ({ cashFlowData, portfolioTrackerId, propertyName, setIsEditable, onCancel }) => {
  const [selectedProperty, setSelectedProperty] = useState<string>(portfolioTrackerId?.toString() || "");
  const [tableData, setTableData] = useState<PaymentRow[]>([
    {
      id: 0,
      dateReceived: "",
      dateFrom: "",
      dateTo: "",
      amount: undefined,
      notes: "",
      invalidFields: [],
      isNewRow: true,
    },
  ]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const toastRef = useRef<Messages>(null);
  const memberId = localStorage.getItem(MEMBER_KEY);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");

  const propertyOptions = !portfolioTrackerId
    ? cashFlowData?.PropertyDetails.map((property) => ({
        label: [
          property.PropertyDetail.StreetAddress,
          property.PropertyDetail.Suburb,
          property.PropertyDetail.State,
          property.PropertyDetail.Postcode,
        ]
          .filter(Boolean)
          .join(", "),
        value: property.PropertyDetail.PortfolioTrackerId,
      }))
    : [
        {
          label: propertyName,
          value: portfolioTrackerId,
        },
      ];

  const hideDefaultProfile = () => {
    setShowConfirmationDialog(false);
  };
  const showToast = (severity: "success" | "info" | "warn" | "error", summary: string, detail: string) => {
    toastRef.current?.show({
      severity,
      summary,
      detail,
      life: 2000,
    });
  };

  const isValidDate = (dateString: string): boolean => {
    if (!dateString || dateString === "") return false;
    return true;
  };

  const formatDate = (dateString: string): string => {
    if (!isValidDate(dateString)) return "";
    return getDateStringWithGivenFormat(new Date(dateString), "dd/mm/yy");
  };

  const ValidateData = (): boolean => {
    let isValid: boolean = true;
    const today = new Date();
    const startOfFinancialYear = getStartOfFinancialYear();
    let validationFields: string[] = [];

    if (selectedProperty == "" && !portfolioTrackerId) {
      isValid = false;
      if (toastRef.current) {
        showToast("error", "Error", "Please select a property");
      }
    }

    const updatedTableData = tableData.map((row) => {
      let rowInvalidFields: string[] = [];

      if (row.id === 0) {
        if (!row.dateReceived || !row.dateFrom || !row.amount) {
          if (!row.dateReceived) {
            validationFields.push("Date Received");
            rowInvalidFields.push("dateReceived");
          }

          if (!row.amount) {
            validationFields.push("Amount");
            rowInvalidFields.push("amount");
          }

          if (rowInvalidFields && rowInvalidFields.length > 0) ValidationMessege(rowInvalidFields);
          isValid = rowInvalidFields.length === 0 ? true : false;
        }

        if (!row.amount || row.amount <= 0) {
          rowInvalidFields.push("amount");
          isValid = false;
          showToast("error", "Error", "Please enter a valid amount greater than 0.");
        }

        if (row.dateFrom && row.dateTo) {
          const dateFrom = new Date(row.dateFrom);
          const dateTo = new Date(row.dateTo);
          if (dateFrom >= dateTo) {
            rowInvalidFields.push("dateFrom");
            rowInvalidFields.push("dateTo");
            isValid = false;
            showToast("error", "Error", "Date from should be less than date to.");
          }
        }

        if (
          row.dateReceived &&
          (new Date(row.dateReceived) < startOfFinancialYear || new Date(row.dateReceived) > today)
        ) {
          const containsDateReceived = validationFields.reduce((acc, curr) => {
            return acc || curr === "Date Received";
          }, false);
          !containsDateReceived && rowInvalidFields.push("dateReceived");
          isValid = false;
          showToast(
            "error",
            "Error",
            "Rental income date must be between the start of the current financial year and today."
          );
        }
      }

      return { ...row, invalidFields: rowInvalidFields };
    });

    setTableData(updatedTableData);

    if (isValid) {
      return true;
    } else {
      return false;
    }
  };

  const handleAddRow = (row: PaymentRow) => {
    if (ValidateData()) {
      const newRow: PaymentRow = {
        id: tableData.length + 1,
        dateReceived: "",
        dateFrom: "",
        dateTo: "",
        amount: undefined,
        notes: "",
        isNewRow: true,
      };

      const updatedTableData = [...tableData];
      updatedTableData.splice(row.id, 0, newRow);

      updatedTableData.map((item, index) => {
        if (item.id == row.id) {
          item.address = propertyOptions?.find((p: any) => p.value === parseInt(selectedProperty))?.label || "";
          item.group = "Rental income";
        }
      });

      const finalTableData = updatedTableData.map((item, index) => {
        if (index === 0) {
          return { ...item, id: 0 };
        } else {
          return { ...item, id: index };
        }
      });

      setTableData(finalTableData);
    }
  };

  const handleDeleteRow = (rowId: number, isRowEditable: boolean) => {
    if (rowId === 0 && !isRowEditable && tableData.length == 0) {
      setTableData([
        {
          id: 0,
          dateReceived: "",
          dateFrom: "",
          dateTo: "",
          amount: 0,
          notes: "",
          isNewRow: true,
        },
      ]);
    } else {
      const filteredData = tableData.filter((row) => row.id !== rowId);

      const updatedData = filteredData.map((row, index) => ({
        ...row,
        id: index,
      }));

      setTableData(updatedData);
    }
  };

  const SaveRentData = async () => {
    const rowsToSave = tableData.filter((row) => row.id > 0);
    if (rowsToSave.length === 0) {
      setShowConfirmationDialog(false);
      return;
    }

    const payload = {
      MemberId: memberId,
      MemberCountryCode: memberDetail.Country,
      Actuals: rowsToSave.map((row) => ({
        PortfolioTrackerId: selectedProperty,
        LoanId: 0,
        TrackingItemType: "CashflowRent.RentPA",
        Date: formatDate(row.dateReceived),
        DateFrom: formatDate(row.dateFrom),
        DateTo: formatDate(row.dateTo),
        Amount: row.amount?.toString() || "",
        Description: row.notes,
      })),
      PurchaseCosts: [],
    };

    await httpPost(ApiEndPoints.tracker + "PortfolioTracker/ActualsByBatch/", payload).then((response) => {
      if (response?.error) {
        if (toastRef.current) {
          toastRef.current.show({
            severity: "error",
            summary: "Error Message",
            detail: "Failed to rent details.",
            life: 3000,
          });
        }
      } else {
        if (toastRef.current) {
          toastRef.current.show({
            severity: "success",
            summary: "Success Message",
            detail: "Rent details saved successfully.",
            life: 2000,
          });
        }

        setTableData([
          {
            id: 0,
            dateReceived: "",
            dateFrom: "",
            dateTo: "",
            amount: 0,
            notes: "",
            isNewRow: true,
          },
        ]);
        setIsEditable(false);
      }
      setTimeout(() => {
        setShowConfirmationDialog(false);
      }, 200);
    });
  };

  const handleTableFieldChange = (rowId: number, field: keyof PaymentRow, value: any) => {
    setTableData((prevTableData) =>
      prevTableData.map((row) => (row.id === rowId ? { ...row, [field]: value, invalidFields: [] } : row))
    );
  };

  const dateBodyTemplate = (row: PaymentRow, field: keyof PaymentRow) => (
    <DatePicker
      dateString={row[field] ? row[field].toString() : ""}
      onChange={(e) => handleTableFieldChange(row.id, field, e.value?.toISOString())}
      dateFormat="dd/mm/yy"
      showIcon={true}
      className="w-full"
      invalid={row.invalidFields?.includes(field) ? true : false}
    />
  );

  const renderDateCell = (rowData: any, field: keyof PaymentRow) => (
    <Calendar
      value={rowData[field]}
      onChange={(e) => (rowData[field] = e.value)}
      showIcon
      disabled={rowData.isNew}
      dateFormat="dd/mm/yy"
      readOnlyInput
      className={"w-full"}
      invalid={rowData.invalidFields?.includes(field) ? true : false}
    />
  );

  const ValidationMessege = (fields: string[]) => {
    if (toastRef.current) {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: `Please fill the following fields: ${fields.join(", ")}`,
        life: 2000,
      });
    }
    return;
  };

  const renderTable = (isRowEditable: boolean) => {
    const rowsToDisplay = isRowEditable ? tableData : tableData.filter((row) => row.id > 0);

    return (
      <>
        <DataTable value={rowsToDisplay} className="editable-table p-datatable-striped w-full" size="small">
          <Column field="id" header="" hidden={true} />
          <Column
            field="id"
            header="Item"
            body={(rowData) => {
              const index = tableData.findIndex((row) => row.id === rowData.id);
              if (index === 0) {
                return <span className="p-text-secondary">Rental income</span>;
              } else {
                return <span className="p-text-secondary"></span>;
              }
            }}
          />
          <Column
            field="dateReceived"
            header="Date received(dd/mm/yy)"
            body={(row) =>
              isRowEditable && row.id === 0 ? renderDateCell(row, "dateReceived") : formatDate(row.dateReceived)
            }
          />
          <Column
            field="dateFrom"
            header="Date from (dd/mm/yy)"
            body={(row) => (isRowEditable && row.id === 0 ? renderDateCell(row, "dateFrom") : formatDate(row.dateFrom))}
            bodyClassName={(rowData) => (!rowData.dateFrom ? "p-invalid" : "")}
          />
          <Column
            field="dateTo"
            header="Date to (dd/mm/yy)"
            body={(row) => (isRowEditable && row.id === 0 ? renderDateCell(row, "dateTo") : formatDate(row.dateTo))}
          />
          <Column
            field="amount"
            header="Amount"
            body={(row) =>
              isRowEditable && row.id === 0 ? (
                <REIInputText
                  id={`mcferent-txt-amount-${row.id}`}
                  name="amount"
                  value={row.amount?.toString() || ""}
                  onChange={(e) => handleTableFieldChange(row.id, "amount", parseFloat(e.target.value))}
                  type="number"
                  className={`w-full ${row.amount == 0 ? "p-invalid" : ""}`}
                />
              ) : row.amount ? (
                row.amount.toString()
              ) : (
                ""
              )
            }
          />
          <Column
            field="notes"
            header="Notes"
            body={(row) =>
              isRowEditable && row.id === 0 ? (
                <REIInputText
                  id={`mcferent-txt-notes-${row.id}`}
                  type="text"
                  name="notes"
                  value={row.notes}
                  onChange={(e) =>
                    isRowEditable ? handleTableFieldChange(row.id, "notes", e.target.value) : row.notes
                  }
                  className="w-full"
                />
              ) : (
                row.notes.toString()
              )
            }
          />
          <Column
            header="Action"
            body={(row, options) =>
              options.rowIndex === 0 && isRowEditable ? (
                <REIButton
                  id={`mcferent-btn-addrow-${row.id}`}
                  icon="pi pi-plus"
                  classNames="p-button-text"
                  tooltip="Add Row"
                  onClick={() => {
                    handleAddRow(row);
                  }}
                />
              ) : (
                <REIButton
                  id={`mcferent-btn-removerow-${row.id}`}
                  icon="pi pi-minus"
                  classNames="p-button-text"
                  tooltip="Remove Row"
                  onClick={() => handleDeleteRow(row.id, isRowEditable)}
                />
              )
            }
            headerStyle={{ width: "3rem" }}
          />
        </DataTable>
        <div className="flex justify-end space-x-2 mt-4">
          <REIButton
            id="mcferent-btn-cancel"
            label="Cancel without saving"
            icon="pi pi-times"
            classNames="p-button-secondary"
            onClick={() => {
              showConfirmationDialog && setIsEditable && setIsEditable(false);
              onCancel && onCancel();
              setShowConfirmationDialog(false);
            }}
          />
          {isRowEditable && (
            <REIButton
              id="mcferent-btn-save"
              label="Save"
              icon="pi pi-check"
              classNames="p-button-success"
              onClick={() => {
                if (tableData.length == 1) {
                  if (toastRef.current) {
                    toastRef.current.show({
                      severity: "error",
                      summary: "Error",
                      detail: "Please add at least one row.",
                      life: 3000,
                    });
                  }
                } else {
                  setShowConfirmationDialog(true);
                }
              }}
            />
          )}
          {!isRowEditable && (
            <REIButton
              key="confirm"
              id="mcferent-btn-saveconfirm"
              label="Save"
              icon="pi pi-check"
              classNames="p-button-success"
              onClick={() => {
                SaveRentData();
              }}
            />
          )}
        </div>
      </>
    );
  };

  const renderTableSummary = () => {
    const newrented = false ? tableData : tableData.filter((row) => row.id > 0);
    return (
      <>
        <div className="flex flex-row w-full">
          Please check the following updates you've added before adding these to each of your properties.
        </div>
        <div className="flex flex-row w-full">
          <DataTable
            value={newrented}
            rowGroupMode="rowspan"
            groupRowsBy="address"
            sortMode="single"
            sortField="address"
            className="p-datatable-striped w-full"
            size="small"
          >
            <Column field="address" header="Address" />
            <Column field="group" header="Item" />
            <Column field="date" header="Date" body={(row) => formatDate(row.dateReceived)} />
            <Column field="amount" header="Amount" />
            <Column field="notes" header="Notes" />
            <Column
              header="Action"
              body={(row, options) => (
                <REIButton
                  id={`mcferent-btn-summaryremove-${row.id}`}
                  icon="pi pi-minus"
                  classNames="p-button-text"
                  tooltip="Remove Row"
                  onClick={() => handleDeleteRow(row.id, false)}
                />
              )}
            />
          </DataTable>
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <REIButton
            id="mcferent-btn-goback"
            label="Go back and edit"
            icon="pi pi-times"
            classNames="p-button-secondary"
            onClick={() => {
              setShowConfirmationDialog(false);
            }}
          />
          <REIButton
            id="mcferent-btn-summarysave"
            label="Save"
            icon="pi pi-check"
            classNames="p-button-success"
            onClick={SaveRentData}
          />
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="w-full">
        <Messages ref={toastRef}></Messages>
      </div>
      <REIDialoge
        showHeader={true}
        header="Review and confirm your actuals updates"
        visible={showConfirmationDialog}
        handleEditClick={hideDefaultProfile}
        position="top"
        style={{ width: "60vw" }}
        customClass="edit-default-profile"
        contentComponent={<div>{renderTableSummary()}</div>}
      ></REIDialoge>
      {!portfolioTrackerId && (
        <div>
          <div className="flex flex-row flex-wrap">
            <label className="self-center mx-3" htmlFor="property">
              Select Property
            </label>
            <REIDropDown
              id="mcferent-drop-property"
              key={`property-${selectedProperty}`}
              value={selectedProperty}
              data={propertyOptions || []}
              label="label"
              onSelectionChange={(e) => setSelectedProperty(e.value)}
              placeholder="Select Property"
              className="w-min"
            />
          </div>
        </div>
      )}

      <div className="flex-1">{renderTable(true)}</div>
      <REIDivider></REIDivider>
      <div id="footerNote">
        Please note that transactions manually entered will not appear within Xero. When using Xero, always add your
        transactions in Xero, and then Sync to Portfolio Tracker. Sync is one way only, Xero to Portfolio Tracker.
      </div>
    </div>
  );
};

export default RentEditForm;
